import { IonIcon } from '@ionic/react';
import { motion, useAnimation } from 'framer-motion';
import { notificationsOutline } from 'ionicons/icons';
import React, { useEffect, useRef } from 'react';
import { createUseStyles } from 'react-jss';

const BADGE_DURATION = 0.83;

const useStyles = createUseStyles({
  icon: {
    fontSize: '24px',
    cursor: 'pointer',
    color: 'rgba(var(--ion-color-dark-rgb), 0.45)',
    transform: 'translateY(-1px)',
  },
  jingle: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: 12,
    transition: 'background-color 120ms ease',
    '&:hover': {
      backgroundColor: 'rgba(var(--ion-color-dark-rgb), 0.055)',
    },
  },
  jiggle: {
    display: 'flex',
    transformOrigin: 'top center',
  },
  badge: {
    position: 'absolute',
    top: 3,
    right: 3,
    width: 9,
    height: 9,
    borderRadius: '50%',
    backgroundColor: 'var(--ion-color-secondary)',
    pointerEvents: 'none',
  },
});

interface NotificationsToggleProps {
  onClick: (event: any) => void;
  unreadCount: number;
}

const NotificationsToggle: React.FC<NotificationsToggleProps> = ({ onClick, unreadCount }) => {
  const classes = useStyles();
  const hasUnread = unreadCount > 0;
  const bellControls = useAnimation();
  const badgeControls = useAnimation();
  const prevUnreadCount = useRef(unreadCount);

  useEffect(() => {
    if (unreadCount > prevUnreadCount.current) {
      bellControls.set({ rotate: -20 });
      bellControls.start({
        rotate: 0,
        transition: { type: 'spring', stiffness: 170, damping: 6 },
      });

      badgeControls.set({ scale: 0 });
      badgeControls.start({
        scale: [0, 1.15, 1, 1, 0],
        transition: {
          duration: BADGE_DURATION,
          times: [0, 0.3, 0.5, 0.75, 1],
          ease: 'easeInOut',
        },
      });
    }
    prevUnreadCount.current = unreadCount;
  }, [unreadCount, bellControls, badgeControls]);

  return (
    <span className={classes.jingle}>
      <motion.span className={classes.jiggle} animate={bellControls}>
        <IonIcon icon={notificationsOutline} onClick={onClick} className={classes.icon} />
      </motion.span>
      {hasUnread && (
        <motion.span className={classes.badge} initial={{ scale: 0 }} animate={badgeControls} />
      )}
    </span>
  );
};

export default NotificationsToggle;
